import { Type, type Static } from "@sinclair/typebox";
import { UuidSchema } from "./identifiers.ts";
import { JsonObjectSchema, type JsonWireObject } from "./json-value.ts";

export const KNOWLEDGE_PROCESSING_SCHEMA_VERSION = "1.0" as const;

export const KNOWLEDGE_AUTOMATION_KINDS = [
  "document_processing",
  "document_unitization",
  "business_view_extraction",
  "embedding_reconcile",
  "leaf_conceptization",
  "concept_hierarchy_build"
] as const;

export const KNOWLEDGE_WORK_KINDS = [
  "semantic_unitization",
  "business_view",
  "raw_unit_embedding",
  "statement_embedding",
  "query_embedding",
  "statement_graph_embedding",
  "leaf_embedding",
  "leaf_conceptization",
  "concept_hierarchy",
  "search_projection"
] as const;

export const KNOWLEDGE_EMBEDDING_WORK_KINDS = [
  "raw_unit_embedding",
  "statement_embedding",
  "query_embedding",
  "statement_graph_embedding",
  "leaf_embedding"
] as const;

export type KnowledgeAutomationKind = (typeof KNOWLEDGE_AUTOMATION_KINDS)[number];
export type KnowledgeWorkKind = (typeof KNOWLEDGE_WORK_KINDS)[number];
export type KnowledgeEmbeddingWorkKind = (typeof KNOWLEDGE_EMBEDDING_WORK_KINDS)[number];

const WorkerDependencyStatusSchema = Type.Union([
  Type.Literal("healthy"),
  Type.Literal("unavailable")
]);

export const IngestionWorkerHealthSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    status: Type.Union([Type.Literal("healthy"), Type.Literal("degraded")]),
    singletonLockHeld: Type.Boolean(),
    migrationReady: Type.Boolean(),
    dependencies: Type.Object(
      {
        postgres: WorkerDependencyStatusSchema,
        documentPostgres: WorkerDependencyStatusSchema,
        platformApi: WorkerDependencyStatusSchema,
        derivedAsset: WorkerDependencyStatusSchema,
        openSearch: WorkerDependencyStatusSchema,
        docling: WorkerDependencyStatusSchema,
        embedding: WorkerDependencyStatusSchema,
        llm: WorkerDependencyStatusSchema
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);
export type IngestionWorkerHealthV1 = Static<typeof IngestionWorkerHealthSchema>;

const TimestampSchema = Type.String({ format: "date-time" });
const ChecksumSchema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const SubjectIdSchema = Type.String({ minLength: 1, maxLength: 512 });
const RepresentationSpaceIdSchema = Type.String({ minLength: 1, maxLength: 256 });

export const CurrentKnowledgeBaseSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    knowledgeBaseId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 256 }),
    description: Type.String({ maxLength: 4096 }),
    sourceFileSpaceId: Type.Union([UuidSchema, Type.Null()]),
    currentLeafConceptizationResultId: Type.Union([UuidSchema, Type.Null()]),
    currentHierarchySnapshotId: Type.Union([UuidSchema, Type.Null()]),
    revision: Type.Integer({ minimum: 0 }),
    createdBy: Type.String({ minLength: 1, maxLength: 256 }),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type CurrentKnowledgeBaseV1 = Static<typeof CurrentKnowledgeBaseSchema>;

export const CurrentKnowledgeBaseCreateInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    knowledgeBaseId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 256, pattern: "\\S" }),
    description: Type.String({ minLength: 1, maxLength: 4096, pattern: "\\S" }),
    sourceFileSpaceId: UuidSchema,
    sourceFilter: Type.Optional(
      Type.Object(
        {
          rootPath: Type.String({ maxLength: 1024 }),
          includeFileExtensions: Type.Array(
            Type.Union([Type.Literal("pdf"), Type.Literal("docx"), Type.Literal("md")]),
            { minItems: 1, maxItems: 3, uniqueItems: true }
          )
        },
        { additionalProperties: false }
      )
    )
  },
  { additionalProperties: false }
);
export type CurrentKnowledgeBaseCreateInputV1 = Static<
  typeof CurrentKnowledgeBaseCreateInputSchema
>;

export const CurrentKnowledgeBaseUpdateInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    name: Type.String({ minLength: 1, maxLength: 256 }),
    description: Type.String({ maxLength: 4096 })
  },
  { additionalProperties: false }
);
export type CurrentKnowledgeBaseUpdateInputV1 = Static<
  typeof CurrentKnowledgeBaseUpdateInputSchema
>;

export const CurrentConceptForestSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    leafConceptizationResultId: UuidSchema,
    hierarchySnapshotId: Type.Union([UuidSchema, Type.Null()]),
    counts: Type.Object(
      {
        leaf: Type.Integer({ minimum: 0 }),
        parent: Type.Integer({ minimum: 0 }),
        root: Type.Integer({ minimum: 0 }),
        ownedStatements: Type.Integer({ minimum: 0 }),
        pendingStatements: Type.Integer({ minimum: 0 })
      },
      { additionalProperties: false }
    ),
    nodes: Type.Array(
      Type.Object(
        {
          conceptId: SubjectIdSchema,
          conceptKind: Type.Union([Type.Literal("leaf"), Type.Literal("parent")]),
          canonicalName: Type.String({ minLength: 1, maxLength: 512 }),
          groundedSummary: Type.String({ minLength: 1, maxLength: 4000 }),
          statementCount: Type.Integer({ minimum: 0 })
        },
        { additionalProperties: false }
      )
    ),
    edges: Type.Array(
      Type.Object(
        {
          parentConceptId: SubjectIdSchema,
          childConceptId: SubjectIdSchema,
          ordinal: Type.Integer({ minimum: 0 })
        },
        { additionalProperties: false }
      )
    )
  },
  { additionalProperties: false }
);
export type CurrentConceptForestV1 = Static<typeof CurrentConceptForestSchema>;

const CurrentStatementSourceFields = {
  businessViewResultId: UuidSchema,
  statementId: SubjectIdSchema,
  statementText: Type.String({ minLength: 1, maxLength: 4096 }),
  unitId: SubjectIdSchema,
  unitTitle: Type.Union([Type.String({ maxLength: 512 }), Type.Null()]),
  unitText: Type.String({ minLength: 1 }),
  documentId: UuidSchema,
  documentName: Type.String({ minLength: 1, maxLength: 512 }),
  documentPath: Type.String({ minLength: 1, maxLength: 1024 })
};

export const CurrentConceptStatementSchema = Type.Object(
  {
    ...CurrentStatementSourceFields,
    membershipScore: Type.Number({ minimum: 0, maximum: 1 })
  },
  { additionalProperties: false }
);
export type CurrentConceptStatementV1 = Static<typeof CurrentConceptStatementSchema>;

export const CurrentConceptStatementsSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    conceptId: SubjectIdSchema,
    items: Type.Array(CurrentConceptStatementSchema)
  },
  { additionalProperties: false }
);
export type CurrentConceptStatementsV1 = Static<typeof CurrentConceptStatementsSchema>;

export const CurrentPendingDispositionSchema = Type.Object(
  {
    ...CurrentStatementSourceFields,
    reasonCode: Type.String({ minLength: 1, maxLength: 128 }),
    reasonDetails: JsonObjectSchema
  },
  { additionalProperties: false }
);
export type CurrentPendingDispositionV1 = Static<typeof CurrentPendingDispositionSchema>;

export const CurrentPendingDispositionsSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    leafConceptizationResultId: UuidSchema,
    items: Type.Array(CurrentPendingDispositionSchema)
  },
  { additionalProperties: false }
);
export type CurrentPendingDispositionsV1 = Static<typeof CurrentPendingDispositionsSchema>;

export const CurrentSemanticUnitSchema = Type.Object(
  {
    unitizationResultId: UuidSchema,
    unitId: SubjectIdSchema,
    sequence: Type.Integer({ minimum: 0 }),
    title: Type.Union([Type.String({ maxLength: 512 }), Type.Null()]),
    originalText: Type.String({ minLength: 1 }),
    sourceSpans: Type.Array(Type.Unknown()),
    documentId: UuidSchema,
    documentName: Type.String({ minLength: 1, maxLength: 512 }),
    documentPath: Type.String({ minLength: 1, maxLength: 1024 })
  },
  { additionalProperties: false }
);
export type CurrentSemanticUnitV1 = Static<typeof CurrentSemanticUnitSchema>;

export const CurrentContentPageQuerySchema = Type.Object(
  {
    offset: Type.Optional(Type.Integer({ minimum: 0, default: 0 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200, default: 50 })),
    /** When set, only return content for this Document (e.g. unitization stage review). */
    documentId: Type.Optional(UuidSchema),
    /** When set, only return content for this Semantic Unit (e.g. business-view stage review). */
    unitId: Type.Optional(SubjectIdSchema),
    unitizationResultId: Type.Optional(UuidSchema)
  },
  { additionalProperties: false }
);
export type CurrentContentPageQueryV1 = Static<typeof CurrentContentPageQuerySchema>;

export const CurrentSemanticUnitPageSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    documentCount: Type.Integer({ minimum: 0 }),
    total: Type.Integer({ minimum: 0 }),
    items: Type.Array(CurrentSemanticUnitSchema)
  },
  { additionalProperties: false }
);
export type CurrentSemanticUnitPageV1 = Static<typeof CurrentSemanticUnitPageSchema>;

const CurrentBusinessStatementSchema = Type.Object(
  {
    statementId: SubjectIdSchema,
    text: Type.String({ minLength: 1, maxLength: 4096 }),
    normalizedWeight: Type.Number({ minimum: 0, maximum: 1 })
  },
  { additionalProperties: false }
);
const CurrentPossibleQuerySchema = Type.Object(
  {
    queryId: SubjectIdSchema,
    text: Type.String({ minLength: 1, maxLength: 4096 }),
    statementIds: Type.Array(SubjectIdSchema, { minItems: 1 })
  },
  { additionalProperties: false }
);
const CurrentBusinessViewFields = {
  businessViewResultId: UuidSchema,
  unitizationResultId: UuidSchema,
  unitId: SubjectIdSchema,
  unitTitle: Type.Union([Type.String({ maxLength: 512 }), Type.Null()]),
  unitText: Type.String({ minLength: 1 }),
  documentId: UuidSchema,
  documentName: Type.String({ minLength: 1, maxLength: 512 }),
  documentPath: Type.String({ minLength: 1, maxLength: 1024 }),
  statements: Type.Array(CurrentBusinessStatementSchema),
  possibleQueries: Type.Array(CurrentPossibleQuerySchema)
};
export const CurrentBusinessViewSchema = Type.Union([
  Type.Object(
    {
      ...CurrentBusinessViewFields,
      status: Type.Literal("accepted"),
      failure: Type.Null()
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      ...CurrentBusinessViewFields,
      status: Type.Literal("raw_only"),
      failure: Type.Object(
        {
          reasonCode: Type.String({ minLength: 1, maxLength: 128 }),
          details: JsonObjectSchema
        },
        { additionalProperties: false }
      )
    },
    { additionalProperties: false }
  )
]);
export type CurrentBusinessViewV1 = Static<typeof CurrentBusinessViewSchema>;

export const CurrentBusinessViewPageSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    total: Type.Integer({ minimum: 0 }),
    statementCount: Type.Integer({ minimum: 0 }),
    possibleQueryCount: Type.Integer({ minimum: 0 }),
    items: Type.Array(CurrentBusinessViewSchema)
  },
  { additionalProperties: false }
);
export type CurrentBusinessViewPageV1 = Static<typeof CurrentBusinessViewPageSchema>;

export const CurrentEmbeddingCoverageSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    channels: Type.Array(
      Type.Object(
        {
          workKind: Type.Union(KNOWLEDGE_EMBEDDING_WORK_KINDS.map((kind) => Type.Literal(kind))),
          representationSpaceId: Type.Union([RepresentationSpaceIdSchema, Type.Null()]),
          expectedCount: Type.Integer({ minimum: 0 }),
          completedCount: Type.Integer({ minimum: 0 }),
          missingCount: Type.Integer({ minimum: 0 }),
          completedSampleIds: Type.Array(SubjectIdSchema, { maxItems: 5 }),
          missingSampleIds: Type.Array(SubjectIdSchema, { maxItems: 5 })
        },
        { additionalProperties: false }
      ),
      { minItems: 5, maxItems: 5 }
    )
  },
  { additionalProperties: false }
);
export type CurrentEmbeddingCoverageV1 = Static<typeof CurrentEmbeddingCoverageSchema>;

export const SemanticUnitizationWorkInputSchema = Type.Object(
  { representationId: UuidSchema },
  { additionalProperties: false }
);
export const BusinessViewWorkInputSchema = Type.Object(
  {
    unitizationResultId: UuidSchema,
    unitId: SubjectIdSchema
  },
  { additionalProperties: false }
);
export const RawUnitEmbeddingWorkInputSchema = Type.Object(
  {
    unitizationResultId: UuidSchema,
    unitId: SubjectIdSchema,
    representationSpaceId: RepresentationSpaceIdSchema
  },
  { additionalProperties: false }
);
export const StatementEmbeddingWorkInputSchema = Type.Object(
  {
    businessViewResultId: UuidSchema,
    statementId: SubjectIdSchema,
    representationSpaceId: RepresentationSpaceIdSchema
  },
  { additionalProperties: false }
);
export const QueryEmbeddingWorkInputSchema = Type.Object(
  {
    businessViewResultId: UuidSchema,
    queryId: SubjectIdSchema,
    representationSpaceId: RepresentationSpaceIdSchema
  },
  { additionalProperties: false }
);
export const StatementGraphEmbeddingWorkInputSchema = Type.Object(
  {
    businessViewResultId: UuidSchema,
    statementId: SubjectIdSchema,
    representationSpaceId: RepresentationSpaceIdSchema
  },
  { additionalProperties: false }
);
export const LeafEmbeddingWorkInputSchema = Type.Object(
  {
    leafConceptizationResultId: UuidSchema,
    conceptDefinitionId: UuidSchema,
    representationSpaceId: RepresentationSpaceIdSchema
  },
  { additionalProperties: false }
);
export const LeafConceptizationWorkInputSchema = Type.Object(
  { leafConceptizationInputId: UuidSchema },
  { additionalProperties: false }
);
export const ConceptHierarchyWorkInputSchema = Type.Object(
  { hierarchyInputId: UuidSchema },
  { additionalProperties: false }
);
export const SearchProjectionWorkInputSchema = Type.Union([
  Type.Object(
    {
      projectionKind: Type.Literal("raw_lexical"),
      unitizationResultId: UuidSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      projectionKind: Type.Literal("raw_unit"),
      unitizationResultId: UuidSchema,
      representationSpaceId: RepresentationSpaceIdSchema,
      embeddingSetChecksum: ChecksumSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      projectionKind: Type.Union([Type.Literal("business_statement"), Type.Literal("query_alias")]),
      businessViewResultId: UuidSchema,
      representationSpaceId: RepresentationSpaceIdSchema,
      embeddingSetChecksum: ChecksumSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      projectionKind: Type.Literal("leaf_concept"),
      leafConceptizationResultId: UuidSchema,
      representationSpaceId: RepresentationSpaceIdSchema,
      embeddingSetChecksum: ChecksumSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      projectionKind: Type.Literal("hierarchy_concept"),
      hierarchySnapshotId: UuidSchema,
      representationSpaceId: RepresentationSpaceIdSchema,
      embeddingSetChecksum: ChecksumSchema
    },
    { additionalProperties: false }
  )
]);

export type SemanticUnitizationWorkInputV1 = Static<typeof SemanticUnitizationWorkInputSchema>;
export type BusinessViewWorkInputV1 = Static<typeof BusinessViewWorkInputSchema>;
export type RawUnitEmbeddingWorkInputV1 = Static<typeof RawUnitEmbeddingWorkInputSchema>;
export type StatementEmbeddingWorkInputV1 = Static<typeof StatementEmbeddingWorkInputSchema>;
export type QueryEmbeddingWorkInputV1 = Static<typeof QueryEmbeddingWorkInputSchema>;
export type StatementGraphEmbeddingWorkInputV1 = Static<
  typeof StatementGraphEmbeddingWorkInputSchema
>;
export type LeafEmbeddingWorkInputV1 = Static<typeof LeafEmbeddingWorkInputSchema>;
export type LeafConceptizationWorkInputV1 = Static<typeof LeafConceptizationWorkInputSchema>;
export type ConceptHierarchyWorkInputV1 = Static<typeof ConceptHierarchyWorkInputSchema>;
export type SearchProjectionWorkInputV1 = Static<typeof SearchProjectionWorkInputSchema>;

export type KnowledgeWorkInputV1 =
  | SemanticUnitizationWorkInputV1
  | BusinessViewWorkInputV1
  | RawUnitEmbeddingWorkInputV1
  | StatementEmbeddingWorkInputV1
  | QueryEmbeddingWorkInputV1
  | StatementGraphEmbeddingWorkInputV1
  | LeafEmbeddingWorkInputV1
  | LeafConceptizationWorkInputV1
  | ConceptHierarchyWorkInputV1
  | SearchProjectionWorkInputV1;

export interface KnowledgeWorkInputByKindV1 {
  semantic_unitization: SemanticUnitizationWorkInputV1;
  business_view: BusinessViewWorkInputV1;
  raw_unit_embedding: RawUnitEmbeddingWorkInputV1;
  statement_embedding: StatementEmbeddingWorkInputV1;
  query_embedding: QueryEmbeddingWorkInputV1;
  statement_graph_embedding: StatementGraphEmbeddingWorkInputV1;
  leaf_embedding: LeafEmbeddingWorkInputV1;
  leaf_conceptization: LeafConceptizationWorkInputV1;
  concept_hierarchy: ConceptHierarchyWorkInputV1;
  search_projection: SearchProjectionWorkInputV1;
}

export const KnowledgeWorkItemStatusSchema = Type.Union([
  Type.Literal("queued"),
  Type.Literal("running"),
  Type.Literal("succeeded"),
  Type.Literal("failed"),
  Type.Literal("obsolete")
]);
export type KnowledgeWorkItemStatus = Static<typeof KnowledgeWorkItemStatusSchema>;

export const ConceptHierarchyWorkProgressPhaseSchema = Type.Union([
  Type.Literal("load"),
  Type.Literal("plan"),
  Type.Literal("cluster"),
  Type.Literal("graft"),
  Type.Literal("publish")
]);
export type ConceptHierarchyWorkProgressPhaseV1 = Static<
  typeof ConceptHierarchyWorkProgressPhaseSchema
>;

export const ConceptHierarchyWorkProgressSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    phase: ConceptHierarchyWorkProgressPhaseSchema,
    round: Type.Integer({ minimum: 0 }),
    maxRounds: Type.Integer({ minimum: 0 }),
    frontierCount: Type.Integer({ minimum: 0 }),
    parentCount: Type.Integer({ minimum: 0 }),
    retainedParentCount: Type.Integer({ minimum: 0 }),
    modelCallCount: Type.Integer({ minimum: 0 }),
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type ConceptHierarchyWorkProgressV1 = Static<typeof ConceptHierarchyWorkProgressSchema>;

export interface KnowledgeWorkItemV1<Kind extends KnowledgeWorkKind = KnowledgeWorkKind> {
  schemaVersion: typeof KNOWLEDGE_PROCESSING_SCHEMA_VERSION;
  workItemId: string;
  knowledgeBaseId: string;
  workKind: Kind;
  subjectId: string;
  input: KnowledgeWorkInputByKindV1[Kind];
  inputChecksum: string;
  status: KnowledgeWorkItemStatus;
  attemptCount: number;
  maxAttempts: number;
  errorCode: string | null;
  errorMessage: string | null;
  errorDetails: Record<string, unknown> | null;
  progress?: ConceptHierarchyWorkProgressV1 | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
}

export const KnowledgeWorkItemPageQuerySchema = Type.Object(
  {
    knowledgeBaseId: Type.Optional(UuidSchema),
    workKind: Type.Optional(Type.Union(KNOWLEDGE_WORK_KINDS.map((kind) => Type.Literal(kind)))),
    status: Type.Optional(KnowledgeWorkItemStatusSchema),
    attentionOnly: Type.Optional(Type.Boolean()),
    createdAfter: Type.Optional(TimestampSchema),
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 1024 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200, default: 50 }))
  },
  { additionalProperties: false }
);
export type KnowledgeWorkItemPageQueryV1 = Static<typeof KnowledgeWorkItemPageQuerySchema>;

export interface KnowledgeWorkItemPageV1 {
  schemaVersion: typeof KNOWLEDGE_PROCESSING_SCHEMA_VERSION;
  items: KnowledgeWorkItemV1[];
  nextCursor: string | null;
}

const KnowledgeWorkItemInvalidStatementIssueSchema = Type.Object(
  {
    statementId: SubjectIdSchema,
    codePoint: Type.String({ pattern: "^U\\+[0-9A-F]{4,6}$" }),
    characterIndex: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);

const KnowledgeWorkItemAffectedUnitSchema = Type.Object(
  {
    documentId: UuidSchema,
    documentName: Type.String({ minLength: 1, maxLength: 512 }),
    documentPath: Type.String({ minLength: 1, maxLength: 1024 }),
    unitizationResultId: UuidSchema,
    unitId: SubjectIdSchema,
    businessViewResultId: UuidSchema,
    isCurrentBusinessView: Type.Boolean(),
    issues: Type.Array(KnowledgeWorkItemInvalidStatementIssueSchema, { minItems: 1 })
  },
  { additionalProperties: false }
);

export const KnowledgeWorkItemRemediationPreviewSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    workItemId: UuidSchema,
    workKind: Type.Literal("leaf_conceptization"),
    issueCode: Type.Literal("LEAF_STATEMENT_TEXT_XML_INVALID"),
    affectedStatementCount: Type.Integer({ minimum: 1 }),
    affectedUnitCount: Type.Integer({ minimum: 1 }),
    units: Type.Array(KnowledgeWorkItemAffectedUnitSchema, { minItems: 1 })
  },
  { additionalProperties: false }
);
export type KnowledgeWorkItemRemediationPreviewV1 = Static<
  typeof KnowledgeWorkItemRemediationPreviewSchema
>;

export const KnowledgeWorkItemRemediationInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    action: Type.Union([Type.Literal("rebuild_upstream"), Type.Literal("ignore_upstream")])
  },
  { additionalProperties: false }
);
export type KnowledgeWorkItemRemediationInputV1 = Static<
  typeof KnowledgeWorkItemRemediationInputSchema
>;

export interface KnowledgeWorkItemRemediationResultV1 {
  schemaVersion: typeof KNOWLEDGE_PROCESSING_SCHEMA_VERSION;
  action: KnowledgeWorkItemRemediationInputV1["action"];
  preview: KnowledgeWorkItemRemediationPreviewV1;
  enqueuedWorkItems: KnowledgeWorkItemV1[];
  ignoredUnitCount: number;
  leafReconciliation: {
    candidateCount: number;
    createdCount: number;
    suppressedCount: number;
  } | null;
}

export const AUTOMATION_INTERVAL_MINUTES = [5, 10, 15, 30, 60, 180, 360, 720, 1440] as const;
const AutomationKindSchema = Type.Union(
  KNOWLEDGE_AUTOMATION_KINDS.map((kind) => Type.Literal(kind))
);
const AutomationIntervalMinutesSchema = Type.Union(
  AUTOMATION_INTERVAL_MINUTES.map((minutes) => Type.Literal(minutes))
);
// Fastify enables scalar coercion. Opaque configuration values must therefore avoid
// the recursive scalar union used by JsonObjectSchema or numbers may become strings.
const AutomationConfigurationSchema = Type.Unsafe<JsonWireObject>({
  type: "object",
  propertyNames: { type: "string", maxLength: 128 },
  maxProperties: 256
});

export const AutomationScheduleSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    automationKind: AutomationKindSchema,
    intervalMinutes: AutomationIntervalMinutesSchema,
    enabled: Type.Boolean(),
    configuration: AutomationConfigurationSchema,
    revision: Type.Integer({ minimum: 0 }),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type AutomationScheduleV1 = Static<typeof AutomationScheduleSchema>;

export const AutomationSchedulePutInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    automationKind: AutomationKindSchema,
    intervalMinutes: AutomationIntervalMinutesSchema,
    enabled: Type.Boolean(),
    configuration: AutomationConfigurationSchema,
    expectedRevision: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type AutomationSchedulePutInputV1 = Static<typeof AutomationSchedulePutInputSchema>;

export const AutomationScheduleRunSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    scheduleRunId: UuidSchema,
    automationKind: AutomationKindSchema,
    triggerKind: Type.Union([Type.Literal("scheduled"), Type.Literal("manual")]),
    status: Type.Union([
      Type.Literal("running"),
      Type.Literal("succeeded"),
      Type.Literal("partial"),
      Type.Literal("failed")
    ]),
    targetCount: Type.Integer({ minimum: 0 }),
    candidateCount: Type.Integer({ minimum: 0 }),
    createdCount: Type.Integer({ minimum: 0 }),
    suppressedCount: Type.Integer({ minimum: 0 }),
    failedCount: Type.Integer({ minimum: 0 }),
    errorCode: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    errorMessage: Type.Union([Type.String({ minLength: 1, maxLength: 2048 }), Type.Null()]),
    startedAt: TimestampSchema,
    finishedAt: Type.Union([TimestampSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type AutomationScheduleRunV1 = Static<typeof AutomationScheduleRunSchema>;

export const AutomationDispatchSummarySchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    automationKind: Type.Union(KNOWLEDGE_AUTOMATION_KINDS.map((kind) => Type.Literal(kind))),
    candidateCount: Type.Integer({ minimum: 0 }),
    createdCount: Type.Integer({ minimum: 0 }),
    suppressedCount: Type.Integer({ minimum: 0 }),
    failedCount: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type AutomationDispatchSummaryV1 = Static<typeof AutomationDispatchSummarySchema>;

export const DocumentUnitizationRebuildInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    documentId: UuidSchema
  },
  { additionalProperties: false }
);
export type DocumentUnitizationRebuildInputV1 = Static<
  typeof DocumentUnitizationRebuildInputSchema
>;

export const BusinessViewRebuildInputSchema = Type.Union([
  Type.Object(
    {
      schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
      documentId: UuidSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
      unitId: SubjectIdSchema
    },
    { additionalProperties: false }
  )
]);
export type BusinessViewRebuildInputV1 = Static<typeof BusinessViewRebuildInputSchema>;

export const EmbeddingRebuildInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION),
    workKind: Type.Union(KNOWLEDGE_EMBEDDING_WORK_KINDS.map((kind) => Type.Literal(kind))),
    subjectId: SubjectIdSchema
  },
  { additionalProperties: false }
);
export type EmbeddingRebuildInputV1 = Static<typeof EmbeddingRebuildInputSchema>;

export const LeafConceptizationRebuildInputSchema = Type.Object(
  {
    schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION)
  },
  { additionalProperties: false }
);
export type LeafConceptizationRebuildInputV1 = Static<typeof LeafConceptizationRebuildInputSchema>;

export const ConceptHierarchyRebuildInputSchema = Type.Object(
  { schemaVersion: Type.Literal(KNOWLEDGE_PROCESSING_SCHEMA_VERSION) },
  { additionalProperties: false }
);
export type ConceptHierarchyRebuildInputV1 = Static<typeof ConceptHierarchyRebuildInputSchema>;

export const WorkItemFailureInputSchema = Type.Object(
  {
    errorCode: Type.String({ minLength: 1, maxLength: 128 }),
    errorMessage: Type.String({ minLength: 1, maxLength: 2048 }),
    errorDetails: Type.Union([JsonObjectSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type WorkItemFailureInputV1 = Static<typeof WorkItemFailureInputSchema>;

export const TypedResultGenerationSchema = Type.Object(
  {
    resultId: UuidSchema,
    resultChecksum: ChecksumSchema,
    createdAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type TypedResultGenerationV1 = Static<typeof TypedResultGenerationSchema>;
